/**
 * Error thrown when server returns 403 with gate: "lead_capture".
 * Detected globally by QueryCache to trigger the LeadCaptureModal.
 */
export class LeadGateError extends Error {
  constructor() {
    super("lead_capture");
    this.name = "LeadGateError";
  }
}

function checkLeadGate(status: number, body: string): void {
  if (status === 403) {
    try {
      const json = JSON.parse(body);
      if (json.gate === "lead_capture") throw new LeadGateError();
    } catch (e) {
      if (e instanceof LeadGateError) throw e;
    }
  }
}

/**
 * Typed fetch wrapper for API calls.
 * Throws on non-OK responses with the error body.
 */
export async function apiFetch<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<T> {
  const url = new URL(path, window.location.origin);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const res = await fetch(url.toString(), {
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.text();
    checkLeadGate(res.status, body);
    throw new Error(`API ${res.status}: ${body}`);
  }

  return res.json();
}

/**
 * Typed fetch wrapper for write operations (POST/PUT/DELETE).
 * Throws on non-OK responses with the error body.
 */
export async function apiMutate<T>(
  path: string,
  method: "POST" | "PUT" | "DELETE",
  body?: object
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const text = await res.text();
    checkLeadGate(res.status, text);
    throw new Error(`API ${res.status}: ${text}`);
  }

  return res.json();
}
